import { getAuthToken } from '../auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { navigationRef } from '../navigationService';
import {
  detectLegacyData,
  getMigrationState,
  MIGRATION_STATE_KEY,
} from './VaultMigrationService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('MigrationRouter');

let routed = false;

/**
 * Boot-time check: if the user has a valid auth token and legacy-format data
 * still exists on-device, reset navigation to the VaultMigration screen.
 *
 * Idempotent within a session — fires at most once. Safe to call before the
 * navigation ref is ready (it awaits `isReady`).
 */
export async function routeToMigrationIfNeeded(): Promise<void> {
  if (routed) {
    logger.debug('route skipped — already fired this session');
    return;
  }

  try {
    logger.debug('checking for v1 data...');
    const token = await getAuthToken();
    if (!token) {
      logger.debug('no auth token — skipping (user not logged in)');
      return;
    }

    const state = await getMigrationState();
    if (state.completed) {
      logger.debug('migration already completed on a prior launch — skipping');
      return;
    }

    const plan = await detectLegacyData();
    logger.debug(
      `detectLegacyData: itemsToMigrate=${plan.itemsToMigrate.length}, alreadyMigrated=${plan.alreadyMigrated}, total=${plan.totalCount}`,
    );
    if (plan.itemsToMigrate.length === 0) {
      // Nothing to migrate — mark the state entry so we don't re-check on
      // every cold launch.
      if (state.started) {
        await AsyncStorage.removeItem(MIGRATION_STATE_KEY);
      }
      logger.debug('no v1 ciphertext on device — nothing to migrate');
      return;
    }

    logger.debug(
      `routing to VaultMigration with ${plan.itemsToMigrate.length} item(s)`,
    );
    await waitForNavReady();

    routed = true;
    navigationRef.reset({
      index: 0,
      routes: [{ name: 'VaultMigration' }],
    });
  } catch (err) {
    logger.error('routeToMigrationIfNeeded failed', err);
  }
}

function waitForNavReady(): Promise<void> {
  return new Promise(resolve => {
    const check = () => {
      if (navigationRef.isReady?.()) {
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
}

/** Test/diagnostics helper — reset the once-per-session guard. */
export function __resetMigrationRouterForTests(): void {
  routed = false;
}
